'use client'

import * as React from 'react'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { useSiteDataQuery } from 'codegen/generated/cf'
import { tw } from 'utils/classnames'
import { useTranslations } from 'next-intl'
import { lazyComponent } from 'utils/lazy-component'
import {
  useAIStudioSubscriptionIdQuery,
  useAllAccessSubscriptionIdQuery,
  useCoinsSubscriptionIdQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { useUndoSubscriptionCancel } from '@repo/cf-user/hooks/use-undo-subscription-cancel'
import { BannerContainer } from './container'
import { Countdown } from './countdown'
import { BannerButton } from '../button'
import { useSnackbar } from '../../hooks/use-snackbar'

const Snackbar = lazyComponent(() => import('../snackbar').then(module => module.Snackbar))

type BannerProps = Omit<
  NonNullable<SiteDataQuery['siteData']['undoCancelationBanner']>,
  '__typename' | 'type'
> & {
  type: string
}

interface Props {
  banner: BannerProps
  subscriptionType: 'all-access' | 'studio-ai' | 'coins'
}

export function UndoCancellationBanner({ banner, subscriptionType }: Props) {
  const { text, button, backgroundColor } = banner
  const t = useTranslations('cf_banners')
  const snackbar = useSnackbar()
  const showSnackbar = snackbar.setData
  const { data: allAccessSubscriptionId, refetch: refetchAllAccessSubscription } =
    useAllAccessSubscriptionIdQuery()
  const { data: aiStudioSubscriptionId, refetch: refetchAIStudioSubscription } =
    useAIStudioSubscriptionIdQuery()
  const { data: coinsSubscriptionId, refetch: refetchCoinsSubscription } =
    useCoinsSubscriptionIdQuery()
  const { data: siteData } = useSiteDataQuery()

  const countDownValue = siteData?.siteData?.undoCancelationBanner?.countdown?.value

  let subscriptionId = allAccessSubscriptionId

  if (subscriptionType === 'coins') {
    subscriptionId = coinsSubscriptionId
  }

  if (subscriptionType === 'studio-ai') {
    subscriptionId = aiStudioSubscriptionId
  }

  const [showUndoCancelBanner, setShowUndoCancelBanner] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  const onExtendSubscriptionError = React.useCallback(
    (error: Error) => {
      showSnackbar({
        showSnackbar: true,
        type: 'error',
        text: error.message || t('undo_cancellation_error'),
      })
    },
    [showSnackbar, t],
  )

  const cancel = useUndoSubscriptionCancel(subscriptionId ?? '', onExtendSubscriptionError)

  const restoreSubscription = async () => {
    if (!subscriptionId) {
      return
    }

    setIsLoading(true)

    try {
      await cancel.mutateAsync()
      setShowUndoCancelBanner(true)

      //Refetch the subscription IDs to ensure the cookies are updated with the latest subscription state
      await Promise.all([
        refetchAllAccessSubscription(),
        refetchAIStudioSubscription(),
        refetchCoinsSubscription(),
      ])

      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (error) {
      onExtendSubscriptionError(error as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const onCountdownComplete = () => {
    window.location.reload()
  }

  if (showUndoCancelBanner) {
    return (
      <div className="h-[41px]">
        <p className="bg-secondary-500 text-service-white w-full text-center text-[14px] font-bold uppercase tracking-[0.9px]">
          {t('undo_cancellation_success')}
        </p>
      </div>
    )
  }

  return (
    <>
      <BannerContainer
        className={tw('sticky left-0 top-0 z-[1200] px-0.5')}
        backgroundColor={backgroundColor}
        wrapperClassName="text-16"
        bannerType={banner.type}
      >
        <div className="flex w-full flex-col items-center justify-between md:flex-row">
          <div className="[&_a]:text-cf-white mb-[10px] px-2 text-center text-[16px] font-bold leading-[1.2] md:-mt-[5.5px] md:mb-0 md:p-0 [&_a]:underline">
            {text}
          </div>

          <div className="flex flex-col-reverse items-center justify-center gap-[10px] md:flex-row md:gap-5">
            {countDownValue ? (
              <Countdown
                prefix={banner.countdown.text}
                countdown={countDownValue}
                onComplete={onCountdownComplete}
                className="hidden md:block"
              />
            ) : null}
            {subscriptionId ? (
              <BannerButton loading={isLoading} button={button} onClick={restoreSubscription} />
            ) : null}
          </div>
        </div>
      </BannerContainer>
      {snackbar.show ? (
        <React.Suspense fallback={null}>
          <Snackbar.component type={snackbar.type}>
            {snackbar.type === 'success'
              ? t('undo_cancellation_success')
              : t('undo_cancellation_error')}
          </Snackbar.component>
        </React.Suspense>
      ) : null}
    </>
  )
}
